import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icon'
import { CalendarReservation, ReservationDetailModal, statusChipColor } from './ReservationDetailModal'
import { addDays, isoDate, normalizeDate, normalizeTime, parseISODate, startOfWeek } from './dateUtils'

interface WeekCalendarProps {
  // When set (e.g. a day picked in the month view), the calendar jumps to
  // the week containing that date.
  focusDate?: string | null
}

export const WeekCalendar: React.FC<WeekCalendarProps> = ({ focusDate }) => {
  const { t, i18n } = useTranslation()
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()))
  const [selected, setSelected] = useState<CalendarReservation | null>(null)

  useEffect(() => {
    if (focusDate) setWeekStart(startOfWeek(parseISODate(focusDate)))
  }, [focusDate])

  const dateFrom = isoDate(weekStart)
  const dateTo = isoDate(addDays(weekStart, 6))

  const { data, isLoading } = useQuery({
    queryKey: ['reservations', 'week', dateFrom],
    queryFn: async () => {
      const params = new URLSearchParams({ dateFrom, dateTo, limit: '100' })
      const response = await api.get(`/reservations?${params.toString()}`)
      return response.data.data as CalendarReservation[]
    },
  })

  const reservations = data || []
  const byDate = new Map<string, CalendarReservation[]>()
  for (const r of reservations) {
    const key = normalizeDate(r.reservation_date)
    const list = byDate.get(key) || []
    list.push(r)
    byDate.set(key, list)
  }
  for (const list of byDate.values()) {
    list.sort((a, b) => normalizeTime(a.reservation_time).localeCompare(normalizeTime(b.reservation_time)))
  }

  const today = isoDate(new Date())
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-GB'
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

  const weekEnd = addDays(weekStart, 6)
  const monthFmt = new Intl.DateTimeFormat(locale, { month: 'long' })
  const monthYearFmt = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' })
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth() && weekStart.getFullYear() === weekEnd.getFullYear()
  const monthTitle = sameMonth
    ? cap(monthYearFmt.format(weekStart))
    : weekStart.getFullYear() === weekEnd.getFullYear()
      ? `${cap(monthFmt.format(weekStart))} – ${cap(monthYearFmt.format(weekEnd))}`
      : `${cap(monthYearFmt.format(weekStart))} – ${cap(monthYearFmt.format(weekEnd))}`

  const dayLabel = (d: Date, showMonth: boolean) =>
    `${new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d)} ${d.getDate()}${showMonth ? ` ${new Intl.DateTimeFormat(locale, { month: 'short' }).format(d)}` : ''}`

  const weekTitle = `${new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(weekStart)} – ${new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(weekEnd)}`

  return (
    <div className="bg-[#12121a] border border-white/10 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div>
          <h2 className="font-medium">{monthTitle}</h2>
          <p className="text-xs text-gray-500">{weekTitle}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setWeekStart((w) => addDays(w, -7))}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
            title={t('reservations.prevWeek')}
          >
            <Icon name="chevron-left" className="w-4" />
          </button>
          <button
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg transition-colors text-sm"
          >
            {t('reservations.thisWeek')}
          </button>
          <button
            onClick={() => setWeekStart((w) => addDays(w, 7))}
            className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
            title={t('reservations.nextWeek')}
          >
            <Icon name="chevron-right" className="w-4" />
          </button>
        </div>
      </div>

      {/* Week grid */}
      {isLoading ? (
        <div className="p-8 text-center text-gray-400">{t('reservations.loading')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 md:divide-x md:divide-white/5">
          {Array.from({ length: 7 }, (_, i) => {
            const day = addDays(weekStart, i)
            const key = isoDate(day)
            const dayReservations = byDate.get(key) || []
            const isToday = key === today
            const showMonth = i === 0 || day.getMonth() !== addDays(weekStart, i - 1).getMonth()
            return (
              <div key={key} className={`min-h-[120px] border-b md:border-b-0 border-white/5 ${isToday ? 'bg-[#006cd4]/5' : ''}`}>
                <div className={`px-3 py-2 text-sm font-medium border-b border-white/5 flex items-center gap-2 ${isToday ? 'text-[#006cd4]' : 'text-gray-400'}`}>
                  <span>{dayLabel(day, showMonth)}</span>
                  {isToday && (
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#006cd4]/20 text-[#006cd4]">
                      {t('reservations.today')}
                    </span>
                  )}
                </div>
                <div className="p-2 space-y-1.5">
                  {dayReservations.length === 0 && (
                    <p className="text-xs text-gray-600 px-1">{t('reservations.noReservationsDay')}</p>
                  )}
                  {dayReservations.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setSelected(r)}
                      className={`w-full text-left px-2 py-1.5 rounded-lg border text-xs transition-colors hover:brightness-125 ${statusChipColor(r.status)}`}
                    >
                      <span className="font-medium">{normalizeTime(r.reservation_time)}</span>
                      {' · '}
                      {r.customer_name}
                      {' · '}
                      <Icon name="user" className="w-2.5" /> {r.party_size}
                    </button>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {selected && (
        <ReservationDetailModal reservation={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}
